
import type { World } from "../sim/collision";
import { isqrt } from "../sim/fixed";
import {
  type Gait,
  type MoveInput,
  type MoveState,
  type MoveStats,
  step,
} from "../sim/movement";

export const MAX_UNACKED = 64;
/** Inputs held awaiting acknowledgement -- 3.2 s at 20 Hz. Beyond this the
 *  connection is not merely lagging, it is gone; replaying a longer backlog
 *  produces a violent correction that looks worse than a brief freeze. */

export const REPLAY_BUDGET = 64;
/** Ceiling on replay steps per snapshot, so a pathological backlog cannot stall
 *  the render loop. Hitting it is a dropped-frame event, not a correctness
 *  failure. */

export const TELEPORT_THRESHOLD_MM = 5_000;

/** An input the server has not acknowledged, with the position it was applied
 *  to. The position is what makes a stale snapshot legible: it says where this
 *  client thought it was at that input's sequence, rather than where it has
 *  since predicted itself to be. */
interface Unacked {
  readonly move: MoveInput;
  before: MoveState;
  readonly locked: boolean;
}

/** What reconciling a snapshot did. Useful for telemetry and for tests. */
export interface Correction {
  /** Distance between what the client predicted and where the server put it. */
  readonly errorMm: number;
  /** How many unacknowledged inputs were re-simulated. */
  readonly replayed: number;
  readonly mispredicted: boolean;
  /** The server relocated the player rather than disagreeing with them. */
  readonly teleported: boolean;
}

/** Local prediction for the player's own entity. */
export class Predictor {
  state: MoveState;
  stats: MoveStats;
  world: World;

  private pending: Unacked[] = [];
  private nextSeq = 0;

  lastCorrection: Correction | null = null;
  corrections = 0;
  mispredictions = 0;

  constructor(state: MoveState, stats: MoveStats, world: World) {
    this.state = state;
    this.stats = stats;
    this.world = world;
  }

  // ------------------------------------------------------------------ sending

  pushInput(heading: number, gait: Gait, locked = false): MoveInput {
    const move: MoveInput = { seq: this.nextSeq, heading, gait };
    this.nextSeq += 1;

    // The state *before* the step is kept with the input, not the state after.
    // It is what the client believed when the server was last this far along, so
    // it is the only thing an authoritative position can honestly be compared
    // against -- see `reconcile`.
    const before = this.state;
    this.state = step(this.state, move, this.stats, this.world, 1, locked);
    this.pending.push({ move, before, locked });

    // Drop the oldest rather than refusing new input: if the link is this far
    // gone, the freshest inputs are the ones worth keeping.
    while (this.pending.length > MAX_UNACKED) this.pending.shift();

    return move;
  }

  // --------------------------------------------------------------- reconciling

  /** Fold in a server snapshot and replay whatever it has not yet seen. */
  reconcile(authoritative: MoveState, ackSeq: number): Correction {
    while (this.pending.length > 0 && this.pending[0].move.seq <= ackSeq) {
      this.pending.shift();
    }

    const predicted = this.state;
    const atAck = this.pending.length > 0 ? this.pending[0].before : predicted;
    const teleported = distanceMm(atAck, authoritative) > TELEPORT_THRESHOLD_MM;

    if (teleported) {
      // Every pending input describes a walk from somewhere the player no longer
      // is. Replaying them would carry that walk to the new position -- which on
      // a respawn is a character who arrives in town already several metres into
      // whatever direction they died running.
      this.pending.length = 0;
      this.state = authoritative;
    }

    const replayed = teleported ? 0 : this.replay(authoritative);
    const errorMm = distanceMm(predicted, this.state);
    const correction: Correction = {
      errorMm,
      replayed,
      mispredicted: !teleported && errorMm > 0,
      teleported,
    };

    this.corrections += 1;
    if (correction.mispredicted) this.mispredictions += 1;
    this.lastCorrection = correction;
    return correction;
  }

  /** Re-simulate unacknowledged inputs on top of the server's state. */
  private replay(authoritative: MoveState): number {
    let state = authoritative;
    let replayed = 0;

    for (const entry of this.pending) {
      if (replayed >= REPLAY_BUDGET) break;
      // The kept state is refreshed as we go: it is the baseline the *next*
      // snapshot will be judged against, and after a replay the honest baseline
      // is the replayed one, not the one predicted before the server spoke.
      entry.before = state;
      state = step(state, entry.move, this.stats, this.world, 1, entry.locked);
      replayed += 1;
    }

    this.state = state;
    return replayed;
  }

  // ----------------------------------------------------------------- reporting

  get unacked(): number {
    return this.pending.length;
  }

  /** The sequence of the most recently sent input, or -1 before the first tick.
   *  Actions that are not movement -- a swing -- are stamped with it so the
   *  server can rewind to the frame the player was actually looking at. */
  get lastSeq(): number {
    return this.nextSeq - 1;
  }

  /** Share of snapshots that moved the player. Healthy is near zero. */
  get mispredictionRate(): number {
    if (this.corrections === 0) return 0;
    return this.mispredictions / this.corrections;
  }
}

/** Integer distance, rounded down. No float, no sqrt in the hot path. */
export function distanceMm(a: MoveState, b: MoveState): number {
  const dx = a.xMm - b.xMm;
  const dy = a.yMm - b.yMm;
  if (dx === 0 && dy === 0) return 0;
  return isqrt(dx * dx + dy * dy);
}
